import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import type { VaultNotification } from '../../../../domain/src/lib/notifications/notification.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/notifications';

  list(unreadOnly = false): Observable<VaultNotification[]> {
    const tenantId = resolveTenantId(this.serverContext);
    let q = withTenantParam(tenantId) ?? new HttpParams();
    if (unreadOnly) q = q.set('unread_only', 'true');
    return this.http
      .get<unknown>(this.basePath, {
        headers: tenantHeaders(tenantId),
        params: q,
      })
      .pipe(map((raw) => this.normalizeList(raw)));
  }

  markAsRead(id: string): Observable<unknown> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.put(`${this.basePath}/${encodeURIComponent(id)}/read`, {}, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  markAllAsRead(): Observable<unknown> {
    const tenantId = resolveTenantId(this.serverContext);
    const p = withTenantParam(tenantId);
    return this.http.put(`${this.basePath}/read-all`, {}, {
      headers: tenantHeaders(tenantId),
      ...(p ? { params: p } : {}),
    });
  }

  private normalizeList(raw: unknown): VaultNotification[] {
    let arr: unknown[] = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object') {
      const r = raw as Record<string, unknown>;
      const nested = r['items'] ?? r['notifications'] ?? r['data'];
      if (Array.isArray(nested)) arr = nested;
    }
    return arr.map((item, i) => this.toNotification(item, i));
  }

  private toNotification(item: unknown, fallbackIndex: number): VaultNotification {
    if (!item || typeof item !== 'object') {
      return {
        id: `unknown-${fallbackIndex}`,
        title: 'Notificación',
        message: '',
        created_at: new Date().toISOString(),
      };
    }
    const o = item as Record<string, unknown>;
    const id = String(o['id'] ?? o['notification_id'] ?? `row-${fallbackIndex}`);
    const title = String(o['title'] ?? o['subject'] ?? 'Sin título');
    const message = String(o['message'] ?? o['body'] ?? o['content'] ?? '');
    const created_at = String(o['created_at'] ?? o['createdAt'] ?? new Date().toISOString());
    const read = Boolean(o['read'] ?? o['is_read'] ?? o['read_at']);
    const type = o['type'] != null ? String(o['type']) : undefined;
    const link = o['link'] != null ? String(o['link']) : undefined;
    return {
      id,
      title,
      message,
      read,
      is_read: read,
      type,
      link,
      created_at,
    };
  }
}
